import { useEffect, useMemo, useRef, useState } from 'react';
import {
  cloneRig,
  descendants,
  duration,
  localBind,
  removeJoint,
  renameJoint,
  setJointBind,
  transform,
  uniqueName,
  type RigDoc,
  type RigTransform,
  type Vec3,
} from '../../lib/rig-doc.js';
import { clipDuration, downloadRigGlb, resetPose, useRig, type RigMode } from '../../state/rig.js';
import { useModel } from '../../state/model.js';
import { useScene } from '../../state/scene.js';
import { filesOfKind, useProjectFiles } from '../../state/project-files.js';
import { useWorkspace } from '../../state/workspace.js';
import { useCommands, useKeyHint, useShortcutContext } from '../../lib/shortcuts/index.js';
import { storeHistorySource, undoService } from '../../lib/undo.js';
import { Card, IconButton, NumberField, PropRow, SelectField, TextField, Toggle, VecField } from '../ui/controls.js';
import { AnimationEditors } from './AnimationEditors.js';
import { AutoRigCard, ConstraintsPanel, WeightToolsCard } from './RigPanels.js';
import { RigViewport } from './RigViewport.js';
import './rig-editor.css';

function report(e: unknown) {
  useRig.setState({ error: e instanceof Error ? e.message : String(e) });
}
function dirty() {
  const s = useRig.getState();
  return JSON.stringify(s.rig) !== s.saved;
}
function mayDiscard() {
  return !dirty() || window.confirm('Discard unsaved rig changes?');
}
function mutate(edit: (rig: RigDoc) => void) {
  const s = useRig.getState(),
    rig = cloneRig(s.rig);
  edit(rig);
  s.commit(rig);
}

/** "Label (Key)" for buttons without the IconButton shortcut slot. */
function keyTitle(label: string, key: string): string {
  return key ? `${label} (${key})` : label;
}

/** Frame stepping on the timeline (Blender: Left / Right, Shift+Left to the start). */
function frameCommands() {
  const step = (delta: number) => {
    const s = useRig.getState();
    useRig.setState({ playing: false });
    const end = clipDuration();
    s.seek(Math.max(0, Math.min(end, Math.round((s.time + delta / s.fps) * s.fps) / s.fps)));
  };
  const animating = () => useRig.getState().mode === 'animate' && Boolean(useRig.getState().clip);
  return [
    { id: 'rig.frame.prev', label: 'Previous frame', category: 'Rig & Animate', context: 'model.rig', keys: ['Left'], repeat: true, enabled: animating, run: () => step(-1) },
    { id: 'rig.frame.next', label: 'Next frame', category: 'Rig & Animate', context: 'model.rig', keys: ['Right'], repeat: true, enabled: animating, run: () => step(1) },
    {
      id: 'rig.frame.first',
      label: 'First frame',
      category: 'Rig & Animate',
      context: 'model.rig',
      keys: ['Shift+Left'],
      enabled: animating,
      run: () => {
        useRig.setState({ playing: false });
        useRig.getState().seek(0);
      },
    },
  ];
}

function JointTree() {
  const rig = useRig((s) => s.rig),
    selected = useRig((s) => s.selected);
  const rows: { name: string; depth: number }[] = [];
  const visit = (parent: string | null, depth: number, seen: Set<string>) => {
    for (const joint of rig.joints)
      if ((joint.parent ?? null) === parent && !seen.has(joint.name)) {
        seen.add(joint.name);
        rows.push({ name: joint.name, depth });
        visit(joint.name, depth + 1, seen);
      }
  };
  visit(null, 0, new Set());
  return (
    <div className="rig-joint-tree" role="tree" aria-label="Skeleton joints">
      {rows.map((row) => (
        <button
          type="button"
          role="treeitem"
          aria-level={row.depth + 1}
          aria-selected={row.name === selected}
          className={row.name === selected ? 'is-selected' : ''}
          key={row.name}
          style={{ paddingLeft: 12 + row.depth * 14 }}
          onClick={() => useRig.getState().select(row.name)}
        >
          <span className="rig-joint-dot" />
          {row.name}
        </button>
      ))}
      {!rows.length && (
        <p className="muted">
          Add a root joint, then create children or click the model with Place joint.
        </p>
      )}
    </div>
  );
}

function SkeletonInspector({ add }: { add: (position?: Vec3) => void }) {
  const rig = useRig((s) => s.rig),
    selected = useRig((s) => s.selected);
  const [moveChildren, setMoveChildren] = useState(true);
  const joint = rig.joints.find((j) => j.name === selected),
    excluded = selected ? descendants(rig, selected) : new Set<string>();
  function duplicate(mirror = false) {
    if (!joint) return;
    const next = cloneRig(rig),
      subtree = descendants(rig, joint.name),
      names = new Map<string, string>();
    for (const source of rig.joints.filter((j) => subtree.has(j.name))) {
      let base = `${source.name} copy`;
      if (mirror) {
        base = source.name.replace(
          /Left|Right|\.L\b|\.R\b|_L\b|_R\b/gi,
          (m) =>
            ({ left: 'Right', right: 'Left', '.l': '.R', '.r': '.L', _l: '_R', _r: '_L' })[
              m.toLowerCase()
            ] ?? m,
        );
        if (base === source.name) base = `${source.name} mirrored`;
      }
      const name = uniqueName(base, [...next.joints.map((j) => j.name), ...names.values()]);
      names.set(source.name, name);
    }
    for (const source of rig.joints.filter((j) => subtree.has(j.name))) {
      const bind = transform(source.bind);
      if (mirror) {
        bind.position[0] *= -1;
        bind.rotation[1] *= -1;
        bind.rotation[2] *= -1;
      }
      next.joints.push({
        name: names.get(source.name)!,
        parent: source.parent ? (names.get(source.parent) ?? source.parent) : null,
        bind,
      });
    }
    useRig.getState().commit(next);
    useRig.getState().select(names.get(joint.name)!);
  }
  return (
    <>
      <Card title="Joint hierarchy">
        <div className="rig-button-row">
          <button type="button" className="ghost small" onClick={() => add()}>Add {joint ? 'child' : 'root'}</button>
          <button
            type="button"
            className="ghost small"
            onClick={() => {
              useRig.getState().select(null);
              add([0, 0, 0]);
            }}
          >
            New root
          </button>
        </div>
        <JointTree />
      </Card>
      {joint && (
        <Card title="Bind transform">
          <PropRow label="Name">
            <TextField
              value={joint.name}
              onChange={(name) => {
                try {
                  useRig.getState().commit(renameJoint(rig, joint.name, name.trim()));
                  useRig.getState().select(name.trim());
                } catch (e) {
                  report(e);
                }
              }}
            />
          </PropRow>
          <PropRow label="Parent">
            <SelectField
              value={joint.parent ?? ''}
              options={[{ value: '', label: 'Root' }, ...rig.joints.filter((j) => !excluded.has(j.name)).map((j) => j.name)]}
              onChange={(value) =>
                mutate((r) => {
                  r.joints.find((j) => j.name === joint.name)!.parent = value || null;
                })
              }
            />
          </PropRow>
          <TransformFields
            value={transform(joint.bind)}
            onChange={(bind) => {
              try {
                useRig.getState().commit(setJointBind(rig, joint.name, bind, moveChildren));
              } catch (e) {
                report(e);
              }
            }}
          />
          <div className="rig-check">
            <Toggle checked={moveChildren} onChange={setMoveChildren} label="Move descendants with this joint" />
          </div>
          <p className="muted rig-help">
            Bind transforms use model space. Parenting preserves the joint’s current position.
          </p>
          <div className="rig-button-row">
            <button type="button" className="ghost small" onClick={() => duplicate()}>Duplicate branch</button>
            <button
              type="button"
              className="ghost small"
              onClick={() => duplicate(true)}
              title="Mirror bind joints across the model X axis; clips are kept unchanged"
            >
              Mirror branch
            </button>
            <button
              type="button"
              className="ghost small danger"
              onClick={() => {
                if (window.confirm(`Delete ${joint.name} and its descendants, keys and bindings?`))
                  useRig.getState().commit(removeJoint(rig, joint.name));
              }}
            >
              Delete branch
            </button>
          </div>
        </Card>
      )}
    </>
  );
}

function TransformFields({
  value,
  onChange,
}: {
  value: RigTransform;
  onChange: (t: RigTransform) => void;
}) {
  return (
    <>
      {(['position', 'rotation', 'scale'] as const).map((field) => (
        <PropRow
          key={field}
          label={field === 'rotation' ? 'Rotation °' : field[0]!.toUpperCase() + field.slice(1)}
        >
          <VecField
            value={value[field]}
            onChange={(v) => onChange({ ...value, [field]: v as Vec3 })}
            step={field === 'rotation' ? 1 : 0.01}
          />
        </PropRow>
      ))}
    </>
  );
}

function SkinInspector({
  radius,
  setRadius,
  strength,
  setStrength,
  subtract,
  setSubtract,
  through,
  setThrough,
}: {
  radius: number;
  setRadius: (n: number) => void;
  strength: number;
  setStrength: (n: number) => void;
  subtract: boolean;
  setSubtract: (b: boolean) => void;
  through: boolean;
  setThrough: (b: boolean) => void;
}) {
  const rig = useRig((s) => s.rig),
    geometry = useRig((s) => s.geometry),
    node = useRig((s) => s.node),
    selected = useRig((s) => s.selected);
  const nodes = [...new Set(geometry?.meshes.map((m) => m.node) ?? [])],
    binding = rig.rigidBindings?.[node] ?? '';
  const [mesh, setMesh] = useState(0),
    [vertex, setVertex] = useState(0);
  const indices = geometry?.meshes.map((m, i) => ({ m, i })).filter((v) => v.m.node === node) ?? [];
  const mi = indices[mesh]?.i ?? indices[0]?.i ?? 0;
  const weights = useRig.getState().explicitWeights(node),
    primitive = geometry?.meshes[mi]?.primitive ?? 0,
    current = weights[primitive]?.[vertex] ?? [];
  function editWeight(name: string, weight: number) {
    if (!weights[primitive]?.[vertex]) return;
    const next = cloneRig(rig);
    next.weights ??= {};
    const values = current.filter((w) => w.joint !== name);
    values.push({ joint: name, weight });
    const sum = values.reduce((s, w) => s + w.weight, 0);
    if (sum <= 0) return;
    weights[primitive]![vertex] = values
      .filter((w) => w.weight > 0)
      .sort((a, b) => b.weight - a.weight)
      .slice(0, 4);
    const total = weights[primitive]![vertex]!.reduce((s, w) => s + w.weight, 0);
    weights[primitive]![vertex]!.forEach((w) => (w.weight /= total));
    next.weights[node] = weights;
    if (next.rigidBindings) delete next.rigidBindings[node];
    useRig.getState().commit(next);
  }
  return (
    <>
      <Card title="Skin binding">
        <PropRow label="Mesh node">
          <SelectField
            value={node}
            options={nodes}
            onChange={(value) => {
              useRig.setState({ node: value });
              setMesh(0);
              setVertex(0);
            }}
          />
        </PropRow>
        <PropRow label="Rigid joint">
          <SelectField
            value={binding}
            options={[{ value: '', label: 'Smooth / automatic weights' }, ...rig.joints.map((j) => j.name)]}
            onChange={(value) =>
              mutate((r) => {
                r.rigidBindings ??= {};
                if (value) {
                  r.rigidBindings[node] = value;
                  if (r.weights) delete r.weights[node];
                } else delete r.rigidBindings[node];
              })
            }
          />
        </PropRow>
        <div className="rig-button-row">
          <button
            type="button"
            className="ghost small"
            onClick={() =>
              mutate((r) => {
                if (r.weights) delete r.weights[node];
                if (r.rigidBindings) delete r.rigidBindings[node];
              })
            }
          >
            Auto bind this mesh
          </button>
          <button
            type="button"
            className="ghost small"
            onClick={() => {
              if (
                window.confirm(
                  'Replace all custom weights and rigid bindings with automatic weights?',
                )
              )
                mutate((r) => {
                  r.weights = {};
                  r.rigidBindings = {};
                });
            }}
          >
            Auto bind all
          </button>
        </div>
        <p className="muted rig-help">
          Automatic binding uses the nearest bone segments. Rigid binding attaches a complete mesh
          to one joint.
        </p>
      </Card>
      <WeightToolsCard />
      <Card title="Paint weights">
        <PropRow label="Joint">
          <SelectField
            value={selected ?? undefined}
            options={rig.joints.map((j) => j.name)}
            placeholder="Choose a joint"
            onChange={(value) => useRig.getState().select(value)}
          />
        </PropRow>
        <PropRow label="Brush radius">
          <NumberField value={radius} min={3} max={200} step={1} onChange={setRadius} />
        </PropRow>
        <PropRow label="Strength">
          <NumberField value={strength} min={0.01} max={1} step={0.05} onChange={setStrength} />
        </PropRow>
        <div className="rig-check">
          <Toggle checked={subtract} onChange={setSubtract} label="Subtract weight" />
        </div>
        <div className="rig-check">
          <Toggle checked={through} onChange={setThrough} label="Paint through the mesh" />
        </div>
        <p className="muted rig-help">
          Paint the selected mesh in the preview. Blue is 0, green is 0.5, red is 1. The strongest
          four influences are normalized per vertex.
        </p>
      </Card>
      <Card title="Precise vertex weights">
        <PropRow label="Primitive">
          <SelectField
            value={String(mesh)}
            options={indices.map((v, i) => ({ value: String(i), label: String(v.m.primitive) }))}
            onChange={(value) => {
              setMesh(Number(value));
              setVertex(0);
            }}
          />
        </PropRow>
        <PropRow label="Vertex">
          <NumberField
            value={vertex}
            integer
            min={0}
            max={Math.max(0, (weights[primitive]?.length ?? 1) - 1)}
            onChange={(v) => setVertex(Math.max(0, Math.floor(v)))}
          />
        </PropRow>
        {current.map((w) => (
          <PropRow key={w.joint} label={w.joint}>
            <NumberField
              value={w.weight}
              min={0}
              max={1}
              step={0.05}
              onChange={(v) => editWeight(w.joint, v)}
            />
          </PropRow>
        ))}
        <button
          type="button"
          className="ghost small"
          disabled={!selected}
          onClick={() => {
            if (selected) editWeight(selected, 1);
          }}
        >
          Add selected joint influence
        </button>
        <p className="muted rig-help">
          Weights are tied to the final mesh vertices. Rebind after changing model topology.
        </p>
      </Card>
    </>
  );
}

function AnimationInspector() {
  const rig = useRig((s) => s.rig),
    selected = useRig((s) => s.selected),
    clipName = useRig((s) => s.clip),
    pose = useRig((s) => s.pose),
    time = useRig((s) => s.time),
    autoKey = useRig((s) => s.autoKey);
  const clip = rig.clips?.find((c) => c.name === clipName),
    [copied, setCopied] = useState<RigTransform | null>(null);
  const track = clip?.tracks.find((t) => t.joint === selected),
    key = track?.keys.find((k) => Math.abs(k.time - time) < 1e-5);
  const keyHintSet = useKeyHint('rig.key');
  function addClip() {
    const s = useRig.getState(),
      name = uniqueName(
        'Animation',
        (s.rig.clips ?? []).map((c) => c.name),
      ),
      next = cloneRig(s.rig);
    next.clips ??= [];
    next.clips.push({
      name,
      tracks: next.joints.map((j) => ({
        joint: j.name,
        keys: [
          { time: 0, transform: localBind(next, j) },
          { time: 1, transform: localBind(next, j) },
        ],
      })),
    });
    s.commit(next);
    useRig.setState({ clip: name, time: 0 });
    s.seek(0);
  }
  return (
    <>
      <Card title="Animation clips">
        <div className="rig-button-row">
          <button type="button" className="ghost small" disabled={!rig.joints.length} onClick={addClip}>
            New clip
          </button>
          <button
            type="button"
            className="ghost small"
            disabled={!clip}
            onClick={() => {
              if (!clip) return;
              const name = uniqueName(
                `${clip.name} copy`,
                rig.clips!.map((c) => c.name),
              );
              mutate((r) => r.clips!.push({ ...structuredClone(clip), name }));
              useRig.setState({ clip: name });
              useRig.getState().seek(0);
            }}
          >
            Duplicate
          </button>
          <button
            type="button"
            disabled={!clip}
            className="ghost small danger"
            onClick={() => {
              if (clip && window.confirm(`Delete clip ${clip.name}?`))
                mutate((r) => {
                  r.clips = r.clips!.filter((c) => c.name !== clip.name);
                });
            }}
          >
            Delete
          </button>
        </div>
        <PropRow label="Clip">
          <SelectField
            value={clipName}
            options={[{ value: '', label: 'Select a clip' }, ...(rig.clips ?? []).map((c) => c.name)]}
            onChange={(value) => {
              useRig.setState({ clip: value, playing: false });
              useRig.getState().seek(0);
            }}
          />
        </PropRow>
        {clip && (
          <>
            <PropRow label="Name">
              <TextField
                value={clip.name}
                onChange={(name) => {
                  if (!name.trim() || rig.clips?.some((c) => c.name === name && c !== clip)) {
                    report('Clip names must be unique and nonempty.');
                    return;
                  }
                  mutate((r) => {
                    r.clips!.find((c) => c.name === clip.name)!.name = name;
                  });
                  useRig.setState({ clip: name });
                }}
              />
            </PropRow>
            <PropRow label="Duration (s)">
              <NumberField
                value={duration(clip)}
                min={0.033333}
                step={0.1}
                onChange={(v) => {
                  const end = duration(clip);
                  if (v <= 0 || !end) return;
                  mutate((r) => {
                    for (const t of r.clips!.find((c) => c.name === clip.name)!.tracks)
                      for (const k of t.keys) k.time = (k.time / end) * v;
                  });
                  useRig.getState().seek(Math.min(time, v));
                }}
              />
            </PropRow>
          </>
        )}
      </Card>
      <Card title="Pose">
        <JointTree />
        {selected && pose && clip && (
          <>
            <p className="rig-help muted">
              {selected} · parent-local transform ·{' '}
              {key ? 'Key at playhead' : 'Interpolated / unrecorded pose'}
            </p>
            <TransformFields value={pose} onChange={(v) => useRig.getState().editPose(v)} />
            <div className="rig-check">
              <Toggle checked={autoKey} onChange={(on) => useRig.setState({ autoKey: on })} label="Auto key pose edits" />
            </div>
            <div className="rig-button-row">
              <button type="button" className="primary small" title={keyTitle('Set key', keyHintSet)} onClick={() => useRig.getState().keyPose()}>
                Set key
              </button>
              <button type="button" className="ghost small" onClick={() => useRig.getState().keyPose(true)}>
                Key all joints
              </button>
              <button type="button" className="ghost small" onClick={resetPose}>
                Reset to bind
              </button>
              <button type="button" className="ghost small" onClick={() => setCopied(transform(pose))}>
                Copy pose
              </button>
              <button
                type="button"
                className="ghost small"
                disabled={!copied}
                onClick={() => {
                  if (copied) useRig.getState().editPose(transform(copied));
                }}
              >
                Paste pose
              </button>
            </div>
            <button
              type="button"
              className="ghost small danger"
              disabled={!key}
              onClick={() =>
                mutate((r) => {
                  const c = r.clips!.find((c) => c.name === clipName)!;
                  const t = c.tracks.find((t) => t.joint === selected)!;
                  t.keys = t.keys.filter((k) => Math.abs(k.time - time) >= 1e-5);
                  c.tracks = c.tracks.filter((t) => t.keys.length);
                  if (!c.tracks.length) r.clips = r.clips!.filter((c) => c.name !== clipName);
                })
              }
            >
              Delete key at playhead
            </button>
          </>
        )}
        {!clip && <p className="muted">Create a clip to start posing and recording keyframes.</p>}
      </Card>
    </>
  );
}

function Timeline() {
  const rig = useRig((s) => s.rig),
    clipName = useRig((s) => s.clip),
    time = useRig((s) => s.time),
    playing = useRig((s) => s.playing),
    selected = useRig((s) => s.selected),
    fps = useRig((s) => s.fps),
    loop = useRig((s) => s.loop),
    speed = useRig((s) => s.speed),
    showFrames = useRig((s) => s.showFrames);
  const clip = rig.clips?.find((c) => c.name === clipName),
    end = clipDuration();
  const hints = {
    play: useKeyHint('rig.play'),
    first: useKeyHint('rig.frame.first'),
    prev: useKeyHint('rig.frame.prev'),
    next: useKeyHint('rig.frame.next'),
    key: useKeyHint('rig.key'),
  };
  function seek(value: number) {
    useRig.setState({ playing: false });
    useRig.getState().seek(Math.round(value * fps) / fps);
  }
  return (
    <section className="rig-timeline" aria-label="Animation timeline">
      <div className="rig-transport">
        <SelectField
          value={clipName}
          options={[{ value: '', label: 'Choose clip' }, ...(rig.clips ?? []).map((c) => c.name)]}
          onChange={(value) => {
            useRig.setState({ clip: value, playing: false });
            useRig.getState().seek(0);
          }}
        />
        <IconButton
          icon="step"
          className="rig-flip"
          title="First frame"
          shortcut={hints.first}
          disabled={!clip}
          onClick={() => {
            useRig.setState({ playing: false });
            useRig.getState().seek(0);
          }}
        />
        <IconButton icon="chevronRight" className="rig-flip" title="Previous frame" shortcut={hints.prev} disabled={!clip} onClick={() => seek(Math.max(0, time - 1 / fps))} />
        <IconButton
          icon={playing ? 'pause' : 'play'}
          title={playing ? 'Pause' : 'Play'}
          shortcut={hints.play}
          active={playing}
          disabled={!clip}
          onClick={() => {
            if (!playing && time >= end) useRig.getState().seek(0);
            useRig.setState({ playing: !playing });
          }}
        />
        <IconButton icon="chevronRight" title="Next frame" shortcut={hints.next} disabled={!clip} onClick={() => seek(Math.min(end, time + 1 / fps))} />
        <span className="mono">
          {showFrames ? `${Math.round(time * fps)} / ${Math.round(end * fps)} f` : `${time.toFixed(3)} / ${end.toFixed(3)} s`}
        </span>
        <label>
          Time <NumberField value={time} min={0} step={1 / fps} onChange={seek} />
        </label>
        <label>
          FPS{' '}
          <SelectField value={String(fps)} options={['12', '24', '25', '30', '60']} onChange={(value) => useRig.setState({ fps: Number(value) })} />
        </label>
        <label>
          Speed{' '}
          <SelectField
            value={String(speed)}
            options={[0.25, 0.5, 1, 2].map((n) => ({ value: String(n), label: `${n}×` }))}
            onChange={(value) => useRig.setState({ speed: Number(value) })}
          />
        </label>
        <Toggle checked={loop} onChange={(on) => useRig.setState({ loop: on })} label="Loop" />
        <button type="button" className="ghost small" title={keyTitle('Key selected joint', hints.key)} disabled={!clip || !selected} onClick={() => useRig.getState().keyPose()}>
          + Key
        </button>
      </div>
      <AnimationEditors />
    </section>
  );
}

function SceneRigSetup({ createController }: { createController: () => Promise<string | null> }) {
  const scene = useScene((s) => s.doc),
    scenePath = useScene((s) => s.path),
    path = useRig((s) => s.path),
    rig = useRig((s) => s.rig);
  const [entity, setEntity] = useState(''),
    [rootMotion, setRootMotion] = useState(false),
    [root, setRoot] = useState(''),
    [notice, setNotice] = useState('');
  const entities = useMemo(() => {
    if (!scene || !path) return [];
    const matches = new Set<string>();
    for (const e of scene.entities) {
      const source = (e.components?.mesh as { source?: string } | undefined)?.source;
      if (source?.replace(/^(model|gltf):/, '') !== path) continue;
      let current: typeof e | undefined = e;
      const visited = new Set<string>();
      while (current && !visited.has(current.id)) {
        visited.add(current.id);
        matches.add(current.id);
        current = scene.entities.find((p) => p.id === current?.parent);
      }
    }
    return scene.entities.filter((e) => matches.has(e.id));
  }, [scene, path]);
  const target = entities.some((e) => e.id === entity) ? entity : (entities[0]?.id ?? '');
  async function assign() {
    try {
      const controller = await createController();
      if (!controller || !target) return;
      const e = useScene.getState().doc?.entities.find((e) => e.id === target);
      if (!e) throw new Error('The scene entity is no longer available.');
      const result = useScene
        .getState()
        .apply({
          cmd: 'setComponent',
          id: target,
          key: 'animator',
          value: {
            ...((e.components?.animator as Record<string, unknown>) ?? {}),
            controller,
            applyRootMotion: rootMotion,
            rootBone: root || rig.joints.find((j) => !j.parent)?.name || '',
          },
        });
      if (!result?.ok)
        throw new Error(result && !result.ok ? result.error : 'Could not assign the animator.');
      await useScene.getState().flush();
      if (useScene.getState().saveState === 'error')
        throw new Error('The animator was assigned, but the scene could not be saved.');
      setNotice('Animator assigned and scene saved.');
    } catch (e) {
      report(e);
    }
  }
  return (
    <Card title="Use in scene">
      <p className="muted rig-help">
        {scenePath ?? 'Open a scene that uses this model to assign its Animator here.'}
      </p>
      <PropRow label="Entity">
        <SelectField
          value={target}
          options={[{ value: '', label: 'Matching model entity' }, ...entities.map((e) => ({ value: e.id, label: e.name || e.id }))]}
          onChange={setEntity}
        />
      </PropRow>
      <div className="rig-check">
        <Toggle checked={rootMotion} onChange={setRootMotion} label="Apply root motion" />
      </div>
      <PropRow label="Root joint">
        <SelectField value={root} options={[{ value: '', label: 'First root joint' }, ...rig.joints.map((j) => j.name)]} onChange={setRoot} />
      </PropRow>
      <button type="button" className="ghost small" disabled={!target || !rig.clips?.length} onClick={() => void assign()}>
        Create controller & assign
      </button>
      {notice && <p role="status">{notice}</p>}
      {scene && !entities.length && (
        <p className="muted rig-help">
          The open scene does not reference this model. Set its Mesh source in the Scene inspector
          first.
        </p>
      )}
    </Card>
  );
}

export function RigWorkbench() {
  const s = useRig(),
    projectId = useWorkspace((w) => w.projectId),
    modelPath = useModel((m) => m.path),
    entries = useProjectFiles((p) => p.entries);
  const assets = useMemo(
    () => [
      ...filesOfKind(entries, 'model'),
      ...filesOfKind(entries, 'mesh').filter((p) => p.endsWith('.glb')),
    ],
    [entries],
  );
  const [tool, setTool] = useState<'move' | 'rotate' | 'scale' | 'add'>('move'),
    [axis, setAxis] = useState<'free' | 'x' | 'y' | 'z'>('free'),
    [radius, setRadius] = useState(35),
    [strength, setStrength] = useState(0.15),
    [subtract, setSubtract] = useState(false),
    [through, setThrough] = useState(false),
    [bones, setBones] = useState(true),
    [wire, setWire] = useState(false);
  const [controllerNotice, setControllerNotice] = useState('');
  const prevProject = useRef<number | null>(useRig.getState().projectId),
    fileInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!projectId) return;
    if (prevProject.current !== projectId) {
      prevProject.current = projectId;
      useRig.getState().close();
    }
    if (!useRig.getState().path) {
      const path = modelPath ?? assets[0];
      if (path) void useRig.getState().open(path, projectId);
    }
  }, [projectId, modelPath, assets]);
  useEffect(() => {
    let frame = 0,
      last = 0;
    const tick = (now: number) => {
      frame = requestAnimationFrame(tick);
      const state = useRig.getState();
      if (!state.playing) {
        last = now;
        return;
      }
      if (now - last < 50) return;
      const dt = (now - last) / 1000;
      last = now;
      const end = clipDuration(),
        time = state.time + dt * state.speed;
      if (time >= end && !state.loop) {
        state.seek(end);
        useRig.setState({ playing: false });
      } else state.seek(state.loop ? time % end : time);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      useRig.setState({ playing: false });
    };
  }, []);
  useEffect(() => {
    const before = (e: BeforeUnloadEvent) => {
      if (dirty()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', before);
    return () => window.removeEventListener('beforeunload', before);
  }, []);
  // Keys live in the shortcut registry under `model.rig` (rebindable in Settings › Keyboard Shortcuts);
  // undo / redo go through the shared undo service, so the Undo History panel shows rig steps.
  useShortcutContext('model.rig');
  useEffect(() => undoService.register(storeHistorySource({ id: 'rig', label: 'Rig & Animate', view: 'model', store: useRig })), []);
  useCommands(
    [
      { id: 'rig.save', label: 'Save rig', category: 'Rig & Animate', context: 'model.rig', keys: ['Ctrl+S'], icon: 'check', run: () => void useRig.getState().save() },
      { id: 'rig.tool.move', label: 'Move joint tool', category: 'Rig & Animate', context: 'model.rig', keys: ['W'], icon: 'move', run: () => setTool('move') },
      { id: 'rig.tool.rotate', label: 'Rotate joint tool', category: 'Rig & Animate', context: 'model.rig', keys: ['E'], icon: 'rotate', run: () => setTool('rotate') },
      { id: 'rig.tool.scale', label: 'Scale joint tool', category: 'Rig & Animate', context: 'model.rig', keys: ['R'], icon: 'scale', run: () => setTool('scale') },
      {
        id: 'rig.tool.place',
        label: 'Place joint tool',
        category: 'Rig & Animate',
        context: 'model.rig',
        keys: ['Shift+A'],
        icon: 'plus',
        enabled: () => useRig.getState().mode === 'skeleton',
        run: () => setTool('add'),
      },
      { id: 'rig.axis.x', label: 'Constrain to X axis', category: 'Rig & Animate', context: 'model.rig', keys: ['X'], run: () => setAxis((a) => (a === 'x' ? 'free' : 'x')) },
      { id: 'rig.axis.y', label: 'Constrain to Y axis', category: 'Rig & Animate', context: 'model.rig', keys: ['Y'], run: () => setAxis((a) => (a === 'y' ? 'free' : 'y')) },
      { id: 'rig.axis.z', label: 'Constrain to Z axis', category: 'Rig & Animate', context: 'model.rig', keys: ['Z'], run: () => setAxis((a) => (a === 'z' ? 'free' : 'z')) },
      {
        id: 'rig.play',
        label: 'Play / pause clip',
        category: 'Rig & Animate',
        context: 'model.rig',
        keys: ['Space'],
        icon: 'play',
        enabled: () => useRig.getState().mode === 'animate' && Boolean(useRig.getState().clip),
        run: () => {
          const state = useRig.getState();
          if (!state.playing && state.time >= clipDuration()) state.seek(0);
          useRig.setState({ playing: !state.playing });
        },
      },
      ...frameCommands(),
      {
        id: 'rig.key',
        label: 'Key selected joint',
        category: 'Rig & Animate',
        context: 'model.rig',
        keys: ['I'],
        icon: 'plus',
        enabled: () => useRig.getState().mode === 'animate' && Boolean(useRig.getState().clip && useRig.getState().selected),
        run: () => useRig.getState().keyPose(),
      },
    ],
    [],
  );
  const hints = {
    undo: useKeyHint('edit.undo'),
    redo: useKeyHint('edit.redo'),
    save: useKeyHint('rig.save'),
    move: useKeyHint('rig.tool.move'),
    rotate: useKeyHint('rig.tool.rotate'),
    scale: useKeyHint('rig.tool.scale'),
    add: useKeyHint('rig.tool.place'),
  };
  function add(position?: Vec3) {
    const state = useRig.getState(),
      parent = state.rig.joints.find((j) => j.name === state.selected),
      name = uniqueName(parent ? `${parent.name} child` : 'Root', [
        ...state.rig.joints.map((j) => j.name),
        ...(state.geometry?.nodeNames ?? []),
      ]);
    const bind = transform(parent?.bind);
    bind.position =
      position ??
      (parent ? [bind.position[0], bind.position[1] + 0.25, bind.position[2]] : [0, 0, 0]);
    const next = cloneRig(state.rig);
    next.joints.push({ name, parent: parent?.name ?? null, bind });
    state.commit(next);
    state.select(name);
  }
  function mode(mode: RigMode) {
    useRig.setState({ mode, playing: false });
    useRig.getState().seek(0);
  }
  async function controller() {
    try {
      if (!s.path || !projectId || !s.clip) return null;
      await s.save();
      if (dirty()) return null;
      const path = s.path.replace(/(\.model\.json|\.glb)$/, '') + '.controller.json';
      const { docIO } = await import('../../lib/doc-io.js');
      const { api } = await import('../../lib/api.js');
      const probe = await api.get<{ exists: boolean }>(
        `/projects/${projectId}/exists?path=${encodeURIComponent(path)}`,
      );
      if (probe.exists && !window.confirm(`Replace ${path}?`)) return null;
      const { rig } = useRig.getState();
      await docIO().write(
        projectId,
        path,
        JSON.stringify(
          {
            format: 'prism.controller/1',
            layers: [
              {
                name: 'Base',
                defaultState: s.clip,
                states: (rig.clips ?? []).map((c) => ({
                  name: c.name,
                  motion: c.name,
                  loop: true,
                })),
              },
            ],
          },
          null,
          2,
        ) + '\n',
      );
      useProjectFiles.getState().refreshSoon();
      useRig.setState({ error: null });
      setControllerNotice(`Controller saved: ${path}`);
      return path;
    } catch (e) {
      report(e);
      return null;
    }
  }
  const locked = Boolean(s.geometry?.sourceSkinned && !s.skinUnlocked),
    changed = JSON.stringify(s.rig) !== s.saved;
  return (
    <div className="rig-workbench">
      <div className="rig-toolbar">
        <label className="rig-asset-picker">
          <span className="muted">Model</span>
          <SelectField
            value={s.path ?? undefined}
            options={assets}
            placeholder="Choose a model"
            onChange={(value) => {
              if (projectId && value && mayDiscard()) void s.open(value, projectId);
            }}
          />
        </label>
        <div className="rig-modes" role="tablist" aria-label="Rig editing mode">
          {(['skeleton', 'skin', 'animate'] as const).map((m) => (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={s.mode === m}
              className={`ghost small${s.mode === m ? ' is-on' : ''}`}
              onClick={() => mode(m)}
            >
              {m === 'skeleton' ? 'Skeleton' : m === 'skin' ? 'Skin' : 'Animate'}
            </button>
          ))}
        </div>
        <IconButton icon="undo" title="Undo" shortcut={hints.undo} disabled={!s.past.length || locked} onClick={() => undoService.undo('model')} />
        <IconButton icon="redo" title="Redo" shortcut={hints.redo} disabled={!s.future.length || locked} onClick={() => undoService.redo('model')} />
        <span className="bar-spacer" />
        <span className="rig-save-state">
          {s.saving ? 'Saving…' : changed ? 'Unsaved changes' : 'Saved'}
        </span>
        <button
          type="button"
          className="primary small"
          title={keyTitle('Save rig', hints.save)}
          disabled={!s.valid || s.saving || !s.rig.joints.length || locked}
          onClick={() => void s.save()}
        >
          Save rig
        </button>
        <button type="button" className="ghost small" disabled={!s.valid || !s.rig.joints.length || locked} onClick={downloadRigGlb}>
          Export GLB
        </button>
        <button
          type="button"
          className="ghost small"
          disabled={!s.path || !projectId}
          onClick={() => {
            if (s.path && projectId && mayDiscard()) void s.open(s.path, projectId);
          }}
        >
          Reload
        </button>
      </div>
      <div className="rig-toolbar rig-tools">
        <span className="muted">{s.mode === 'skin' ? 'Weight painting' : 'Joint tools'}</span>
        {(['move', 'rotate', 'scale', 'add'] as const).map((t) => (
          <IconButton
            key={t}
            icon={t === 'add' ? 'plus' : t}
            title={t === 'add' ? 'Place joint' : `${t[0]!.toUpperCase()}${t.slice(1)} joint`}
            shortcut={hints[t]}
            active={tool === t}
            disabled={s.mode === 'skin' || locked || (t === 'add' && s.mode !== 'skeleton')}
            onClick={() => setTool(t)}
          />
        ))}
        <div className="rig-axis">
          <SelectField
            value={axis}
            options={[
              { value: 'free', label: 'View plane / all axes' },
              { value: 'x', label: 'X axis (X)' },
              { value: 'y', label: 'Y axis (Y)' },
              { value: 'z', label: 'Z axis (Z)' },
            ]}
            onChange={(value) => setAxis(value as typeof axis)}
          />
        </div>
        <Toggle checked={bones} onChange={setBones} label="Skeleton overlay" />
        <Toggle checked={wire} onChange={setWire} label="Wireframe" />
        <span className="bar-spacer" />
        <input
          ref={fileInput}
          type="file"
          accept=".json"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file)
              void file.text().then((text) => {
                try {
                  const rig = JSON.parse(text) as RigDoc;
                  if (!Array.isArray(rig.joints))
                    throw new Error('Rig JSON must contain a joints array.');
                  if (mayDiscard()) s.commit(rig);
                } catch (error) {
                  report(error);
                }
              });
            e.target.value = '';
          }}
        />
        <button type="button" className="ghost small" disabled={locked} onClick={() => fileInput.current?.click()}>
          Import rig JSON
        </button>
        <button
          type="button"
          className="ghost small"
          disabled={!s.path}
          onClick={() => {
            const url = URL.createObjectURL(
                new Blob([JSON.stringify(s.rig, null, 2) + '\n'], { type: 'application/json' }),
              ),
              link = document.createElement('a');
            link.href = url;
            link.download = `${s.path?.split('/').at(-1) ?? 'model'}.rig.json`;
            link.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
          }}
        >
          Export rig JSON
        </button>
        <button type="button" className="ghost small" disabled={!s.clip || !s.valid || locked} onClick={() => void controller()}>
          Create controller
        </button>
      </div>
      {s.error && (
        <div className="rig-error" role="alert">
          {s.error}
        </div>
      )}
      {controllerNotice && (
        <div className="rig-notice" role="status">
          {controllerNotice}
        </div>
      )}
      {locked && (
        <div className="rig-error">
          This model has an existing rig. Preview its clips, or make an editable rig copy. Imported
          clips are converted to linear keys at 30 FPS; the original GLB is kept.{' '}
          <button
            type="button"
            className="ghost small"
            onClick={() => {
              useRig.setState({ skinUnlocked: true, saved: '' });
              useRig.getState().commit(cloneRig(s.rig));
            }}
          >
            Edit existing rig
          </button>
        </div>
      )}
      <div className="rig-main">
        <div className="rig-preview-column">
          {s.geometry ? (
            <RigViewport
              tool={tool}
              axis={axis}
              radius={radius}
              strength={strength}
              subtract={subtract}
              through={through}
              showBones={bones}
              wire={wire}
              onAdd={add}
            />
          ) : (
            <div className="rig-empty">
              {s.loading
                ? 'Loading model geometry…'
                : s.path
                  ? 'The rig preview could not be loaded.'
                  : 'Choose a model to start rigging.'}
            </div>
          )}
          {s.mode === 'animate' && <Timeline />}
        </div>
        <aside className="rig-inspector" aria-label="Rig inspector">
          <fieldset disabled={locked || s.loading || !s.engine}>
            {s.mode === 'skeleton' ? (
              <>
                <AutoRigCard />
                <SkeletonInspector add={add} />
                <ConstraintsPanel />
              </>
            ) : s.mode === 'skin' ? (
              <SkinInspector
                radius={radius}
                setRadius={setRadius}
                strength={strength}
                setStrength={setStrength}
                subtract={subtract}
                setSubtract={setSubtract}
                through={through}
                setThrough={setThrough}
              />
            ) : (
              <>
                <AnimationInspector />
                <ConstraintsPanel />
                <SceneRigSetup createController={controller} />
              </>
            )}
          </fieldset>
        </aside>
      </div>
      <footer className="rig-status">
        {s.path ? `${s.path}.rig.json` : 'No rig open'}
        <span>
          {s.rig.joints.length} joints · {s.rig.clips?.length ?? 0} clips ·{' '}
          {s.geometry?.meshes.length ?? 0} mesh primitives
        </span>
      </footer>
    </div>
  );
}
